import {
  HubJobKind,
  type HubJob,
} from '@robovision/hub'
import {
  type DataTag,
  useMutation,
  useQuery,
} from '@tanstack/vue-query'
import {
  computed,
  type MaybeRefOrGetter,
  toValue,
} from 'vue'
import { DriftDetection } from '~domain/driftDetection/DriftDetection'
import { hubApi } from '~services/api'
import { queryClient } from '~services/query-client'
import { RETHROW_ERROR_AFTER_HANDLING } from '~utils/api/error-handling'
import { TEN_SECONDS } from '~utils/api/staleTimes'


export function driftDetectionsQueryKey (
  projectId: string,
): DataTag<unknown[], DriftDetection[]> {
  return [
    'hubApi',
    'jobs',
    'driftDetections',
    projectId,
  ] as DataTag<unknown[], DriftDetection[]>
}

async function fetchDriftDetections (projectId: string): Promise<DriftDetection[]> {
  const { data } = await hubApi.jobs.listJobs({
    filter: [`kind:${HubJobKind.DriftDetection}`],
    projectId,
  }, RETHROW_ERROR_AFTER_HANDLING)

  return data.items.map((job: HubJob) => DriftDetection.fromResponse(job))
}

export function useDriftDetections (projectId: MaybeRefOrGetter<string | undefined>) {
  const projectIdValue = computed(() => toValue(projectId))

  const query = useQuery({
    enabled: computed(() => !!projectIdValue.value),
    queryFn: () => fetchDriftDetections(projectIdValue.value!),
    queryKey: computed(() =>
      (projectIdValue.value
        ? driftDetectionsQueryKey(projectIdValue.value)
        : ['hubApi', 'jobs', 'driftDetections', 'disabled'])),
    staleTime: TEN_SECONDS,
  })

  function invalidateQuery (): void {
    if (!projectIdValue.value) return

    void queryClient.invalidateQueries({
      queryKey: driftDetectionsQueryKey(projectIdValue.value),
    })
  }

  const deleteMutation = useMutation({
    mutationFn: async (jobId: string) => {
      await hubApi.jobs.deleteJob(jobId, RETHROW_ERROR_AFTER_HANDLING)
    },
    onSuccess: invalidateQuery,
  }, queryClient)

  const renameMutation = useMutation({
    mutationFn: async ({
      jobId, name,
    }: {
      jobId: string
      name: string
    }) => {
      await hubApi.jobs.updateJobMetadata(jobId, { name }, RETHROW_ERROR_AFTER_HANDLING)
    },
    onSuccess: invalidateQuery,
  }, queryClient)

  const stopMutation = useMutation({
    mutationFn: async (jobId: string) => {
      await hubApi.jobs.stopJob(jobId, RETHROW_ERROR_AFTER_HANDLING)
    },
    onSuccess: invalidateQuery,
  }, queryClient)

  return {
    deleteDriftDetection: deleteMutation.mutateAsync,
    driftDetections: query.data,
    isLoading: query.isLoading,
    renameDriftDetection: renameMutation.mutateAsync,
    stopDriftDetection: stopMutation.mutateAsync,
  }
}
